import React, { useState, useEffect } from 'react';
import { settingsAPI, companyAPI } from '../services/api';
import Icon from './Icon';

function SuperAdminGlobalSettings({ darkMode = true }) {
  const [settings, setSettings] = useState({});
  const [availableKeys, setAvailableKeys] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  const [testingOpenAI, setTestingOpenAI] = useState(false);
  const [fixingSchema, setFixingSchema] = useState(false);
  const [editValues, setEditValues] = useState({});
  const [showPasswords, setShowPasswords] = useState({});
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await settingsAPI.getAll();
      setSettings(response.data.settings || {});
      setAvailableKeys(response.data.available_keys || {});
      
      // Initialize edit values - DON'T load masked encrypted values
      const initial = {};
      Object.entries(response.data.settings || {}).forEach(([key, setting]) => {
        // For encrypted fields, show placeholder if value exists
        if (setting.type === 'encrypted' && setting.value && setting.value.startsWith('***')) {
          initial[key] = ''; // Leave empty - user must re-enter
        } else {
          initial[key] = setting.value || '';
        }
      });
      setEditValues(initial);
    } catch (err) {
      setError('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      
      // Filter out empty encrypted values (keep existing passwords)
      const valuesToSave = {};
      Object.entries(editValues).forEach(([key, value]) => {
        const config = availableKeys[key];
        // Only include if: not encrypted, OR if encrypted and has a new value
        if (config?.type !== 'encrypted' || (value && value.trim() !== '')) {
          valuesToSave[key] = value;
        }
      });
      
      await settingsAPI.update(valuesToSave);
      setSuccess('Settings saved successfully');
      setTimeout(() => setSuccess(''), 3000);
      fetchSettings();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTestEmail = async () => {
    try {
      setTestingEmail(true);
      setError('');
      const response = await settingsAPI.testEmail();
      if (response.data.success) {
        setSuccess(`Email connected! Inbox has ${response.data.inbox_count} messages`);
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(response.data.error || 'Email connection failed');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to test email');
    } finally {
      setTestingEmail(false);
    }
  };

  const handleTestOpenAI = async () => {
    try {
      setTestingOpenAI(true);
      setError('');
      const response = await settingsAPI.testOpenAI();
      if (response.data.success) {
        setSuccess('OpenAI connected successfully!');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(response.data.error || 'OpenAI connection failed');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to test OpenAI');
    } finally {
      setTestingOpenAI(false);
    }
  };

  const handleFixServicesSchema = async () => {
    try {
      setFixingSchema(true);
      setError('');
      const response = await companyAPI.fixServicesSchema();
      const results = response.data?.results || [];
      const ok = results.filter(r => r.ok).length;
      const errs = results.filter(r => r.error);
      if (errs.length === 0) {
        setSuccess('Services schema fixed. Pull from Square should work now.');
        setTimeout(() => setSuccess(''), 5000);
      } else {
        setError(errs.map(e => `${e.error}: ${e.message}`).join('; ') || 'Schema fix had errors');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fix schema');
    } finally {
      setFixingSchema(false);
    }
  };

  const renderSettingInput = (key, config) => {
    const isPassword = config.type === 'encrypted';
    const showPassword = showPasswords[key];
    const hasExistingValue = settings[key]?.value && settings[key].value.startsWith('***');
    
    return (
      <div key={key} style={{ marginBottom: '16px' }}>
        <label style={{
          display: 'block',
          fontSize: '13px',
          fontWeight: '500',
          color: darkMode ? 'rgba(255,255,255,0.9)' : '#374151',
          marginBottom: '6px'
        }}>
          {key}
          {hasExistingValue && (
            <span style={{ 
              marginLeft: '8px', 
              fontSize: '11px', 
              color: '#22c55e',
              fontWeight: '400'
            }}>
              (configured - leave empty to keep)
            </span>
          )}
        </label>
        <div style={{ display: 'flex', gap: '8px' }}>
          <input
            type={isPassword && !showPassword ? 'password' : 'text'}
            value={editValues[key] || ''}
            onChange={(e) => setEditValues(prev => ({ ...prev, [key]: e.target.value }))}
            placeholder={hasExistingValue ? 'Leave empty to keep existing value' : config.description}
            style={{
              flex: 1,
              padding: '10px 12px',
              borderRadius: '8px',
              border: `1px solid ${hasExistingValue && !editValues[key] ? '#22c55e' : (darkMode ? 'rgba(255,255,255,0.15)' : '#e5e7eb')}`,
              background: darkMode ? 'rgba(0,0,0,0.3)' : '#fff',
              color: darkMode ? '#fff' : '#1f2937',
              fontSize: '14px'
            }}
          />
          {isPassword && (
            <button
              onClick={() => setShowPasswords(prev => ({ ...prev, [key]: !prev[key] }))}
              style={{
                padding: '10px',
                borderRadius: '8px',
                border: `1px solid ${darkMode ? 'rgba(255,255,255,0.15)' : '#e5e7eb'}`,
                background: darkMode ? 'rgba(255,255,255,0.05)' : '#f9fafb',
                color: darkMode ? '#fff' : '#6b7280',
                cursor: 'pointer'
              }}
            >
              <Icon name={showPassword ? 'eye-off' : 'eye'} size={16} />
            </button>
          )}
        </div>
        <p style={{
          fontSize: '12px',
          color: darkMode ? 'rgba(255,255,255,0.5)' : '#9ca3af',
          marginTop: '4px'
        }}>
          {config.description}
        </p>
      </div>
    );
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <div className="spinner" style={{ margin: '0 auto' }}></div>
        <p style={{ marginTop: '12px', color: darkMode ? 'rgba(255,255,255,0.6)' : '#6b7280' }}>
          Loading settings...
        </p>
      </div>
    );
  }

  // Group settings by category
  const emailSettings = ['EMAIL_IMAP_SERVER', 'EMAIL_IMAP_PORT', 'EMAIL_SYNC_ADDRESS', 'EMAIL_IMAP_PASSWORD'];
  const openaiSettings = ['OPENAI_API_KEY', 'OPENAI_MODEL'];
  const processingSettings = ['RECEIPT_CONFIDENCE_THRESHOLD', 'RECEIPT_PROCESSING_ENABLED'];

  return (
    <div style={{ maxWidth: '800px', margin: '0 auto' }}>
      {/* Messages */}
      {error && (
        <div style={{
          background: 'rgba(239, 68, 68, 0.15)',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          borderRadius: '8px',
          padding: '12px 16px',
          marginBottom: '20px',
          color: '#ef4444'
        }}>
          {error}
        </div>
      )}
      {success && (
        <div style={{
          background: 'rgba(16, 185, 129, 0.15)',
          border: '1px solid rgba(16, 185, 129, 0.3)',
          borderRadius: '8px',
          padding: '12px 16px',
          marginBottom: '20px',
          color: '#10b981'
        }}>
          {success}
        </div>
      )}

      {/* Email Settings */}
      <div style={{
        background: darkMode ? 'rgba(255,255,255,0.03)' : '#fff',
        borderRadius: '12px',
        padding: '24px',
        marginBottom: '24px',
        border: `1px solid ${darkMode ? 'rgba(255,255,255,0.08)' : '#e5e7eb'}`
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '10px',
              background: 'rgba(59, 130, 246, 0.15)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Icon name="email" color="primary" darkMode={darkMode} size={20} />
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: darkMode ? '#fff' : '#1f2937' }}>
                Email Configuration
              </h3>
              <p style={{ margin: 0, fontSize: '13px', color: darkMode ? 'rgba(255,255,255,0.5)' : '#6b7280' }}>
                IMAP settings for receiving receipt emails
              </p>
            </div>
          </div>
          <button
            onClick={handleTestEmail}
            disabled={testingEmail}
            style={{
              padding: '8px 16px',
              borderRadius: '8px',
              border: 'none',
              background: 'rgba(59, 130, 246, 0.15)',
              color: '#3b82f6',
              fontSize: '13px',
              fontWeight: '500',
              cursor: testingEmail ? 'wait' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            {testingEmail ? 'Testing...' : 'Test Connection'}
          </button>
        </div>
        
        {emailSettings.map(key => availableKeys[key] && renderSettingInput(key, availableKeys[key]))}
      </div>

      {/* OpenAI Settings */}
      <div style={{
        background: darkMode ? 'rgba(255,255,255,0.03)' : '#fff',
        borderRadius: '12px',
        padding: '24px',
        marginBottom: '24px',
        border: `1px solid ${darkMode ? 'rgba(255,255,255,0.08)' : '#e5e7eb'}`
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '10px',
              background: 'rgba(16, 185, 129, 0.15)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Icon name="ai" color="success" darkMode={darkMode} size={20} />
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: darkMode ? '#fff' : '#1f2937' }}>
                OpenAI Configuration
              </h3>
              <p style={{ margin: 0, fontSize: '13px', color: darkMode ? 'rgba(255,255,255,0.5)' : '#6b7280' }}>
                API settings for AI-powered receipt extraction
              </p>
            </div>
          </div>
          <button
            onClick={handleTestOpenAI}
            disabled={testingOpenAI}
            style={{
              padding: '8px 16px',
              borderRadius: '8px',
              border: 'none',
              background: 'rgba(16, 185, 129, 0.15)',
              color: '#10b981',
              fontSize: '13px',
              fontWeight: '500',
              cursor: testingOpenAI ? 'wait' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            {testingOpenAI ? 'Testing...' : 'Test Connection'}
          </button>
        </div>
        
        {openaiSettings.map(key => availableKeys[key] && renderSettingInput(key, availableKeys[key]))}
      </div>

      {/* Processing Settings */}
      <div style={{
        background: darkMode ? 'rgba(255,255,255,0.03)' : '#fff',
        borderRadius: '12px',
        padding: '24px',
        marginBottom: '24px',
        border: `1px solid ${darkMode ? 'rgba(255,255,255,0.08)' : '#e5e7eb'}`
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
          <div style={{
            width: '40px',
            height: '40px',
            borderRadius: '10px',
            background: 'rgba(245, 158, 11, 0.15)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Icon name="settings" color="warning" darkMode={darkMode} size={20} />
          </div>
          <div>
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: darkMode ? '#fff' : '#1f2937' }}>
              Processing Settings
            </h3>
            <p style={{ margin: 0, fontSize: '13px', color: darkMode ? 'rgba(255,255,255,0.5)' : '#6b7280' }}>
              Receipt processing behavior
            </p>
          </div>
        </div>
        
        {processingSettings.map(key => availableKeys[key] && renderSettingInput(key, availableKeys[key]))}
      </div>

      {/* Database Maintenance */}
      <div style={{
        background: darkMode ? 'rgba(255,255,255,0.03)' : '#fff',
        borderRadius: '12px',
        padding: '24px',
        marginBottom: '24px',
        border: `1px solid ${darkMode ? 'rgba(255,255,255,0.08)' : '#e5e7eb'}`
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '10px',
              background: 'rgba(139, 92, 246, 0.15)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Icon name="database" color="secondary" darkMode={darkMode} size={20} />
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: darkMode ? '#fff' : '#1f2937' }}>
                Fix Pull from Square
              </h3>
              <p style={{ margin: 0, fontSize: '13px', color: darkMode ? 'rgba(255,255,255,0.5)' : '#6b7280' }}>
                Fix &quot;value too long for varchar(10)&quot; error when pulling Square payments
              </p>
            </div>
          </div>
          <button
            onClick={handleFixServicesSchema}
            disabled={fixingSchema}
            style={{
              padding: '8px 16px',
              borderRadius: '8px',
              border: 'none',
              background: 'rgba(139, 92, 246, 0.15)',
              color: '#8b5cf6',
              fontSize: '13px',
              fontWeight: '500',
              cursor: fixingSchema ? 'wait' : 'pointer'
            }}
          >
            {fixingSchema ? 'Fixing...' : 'Fix Schema'}
          </button>
        </div>
      </div>

      {/* Save Button */}
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button
          onClick={handleSave}
          disabled={saving}
          className="btn btn-primary"
          style={{ padding: '12px 24px', fontSize: '14px' }}
        >
          {saving ? 'Saving...' : 'Save All Settings'}
        </button>
      </div>
    </div>
  );
}

export default SuperAdminGlobalSettings;
